const COOKIE_NAME = "aifp_consent";
const LEGACY_STORAGE_KEY = "aifp_cookie_consent";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // ~12 months

export type ConsentChoice = "accepted" | "rejected" | "custom";

export interface CookieConsent {
  necessary: true;
  marketing: boolean;
  choice: ConsentChoice;
}

type ConsentListener = (consent: CookieConsent) => void;
const listeners: Set<ConsentListener> = new Set();

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split("; ");
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

function writeCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === "undefined") return;
  const secure =
    typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function deleteCookie(name: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function encodeConsent(consent: CookieConsent): string {
  if (consent.choice === "accepted") return "accepted";
  if (consent.choice === "rejected") return "rejected";
  return consent.marketing ? "custom:1" : "custom:0";
}

function parseConsentValue(raw: string): CookieConsent | null {
  if (raw === "accepted") {
    return { necessary: true, marketing: true, choice: "accepted" };
  }
  if (raw === "rejected") {
    return { necessary: true, marketing: false, choice: "rejected" };
  }
  if (raw === "custom" || raw === "custom:0") {
    return { necessary: true, marketing: false, choice: "custom" };
  }
  if (raw === "custom:1") {
    return { necessary: true, marketing: true, choice: "custom" };
  }
  try {
    const parsed = JSON.parse(raw) as { marketing?: unknown; choice?: unknown };
    if (typeof parsed?.marketing !== "boolean") return null;
    const choice: ConsentChoice =
      parsed.choice === "accepted" || parsed.choice === "rejected" || parsed.choice === "custom"
        ? parsed.choice
        : parsed.marketing
          ? "accepted"
          : "rejected";
    return { necessary: true, marketing: parsed.marketing, choice };
  } catch {
    return null;
  }
}

function migrateLegacyStorage(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { marketing?: unknown };
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    if (typeof parsed?.marketing !== "boolean") return null;
    const consent: CookieConsent = {
      necessary: true,
      marketing: parsed.marketing,
      choice: parsed.marketing ? "accepted" : "rejected",
    };
    writeCookie(COOKIE_NAME, encodeConsent(consent), MAX_AGE_SECONDS);
    return consent;
  } catch {
    return null;
  }
}

export function getConsent(): CookieConsent | null {
  const fromCookie = readCookie(COOKIE_NAME);
  if (fromCookie) {
    const parsed = parseConsentValue(fromCookie);
    if (parsed) return parsed;
  }
  return migrateLegacyStorage();
}

export function setConsent(consent: CookieConsent): void {
  const next: CookieConsent = {
    necessary: true,
    marketing: consent.marketing,
    choice: consent.choice,
  };
  try {
    writeCookie(COOKIE_NAME, encodeConsent(next), MAX_AGE_SECONDS);
  } catch {
    // cookie write can fail in restricted browsing modes
  }
  for (const fn of listeners) {
    try {
      fn(next);
    } catch {
      // listener errors must not propagate
    }
  }
}

export function hasMarketingConsent(): boolean {
  return getConsent()?.marketing === true;
}

export function onConsentChange(fn: ConsentListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function clearConsent(): void {
  try {
    deleteCookie(COOKIE_NAME);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignore
  }
}
